import { Component, computed, inject, input, resource, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { featureOn } from '../../../../feature-flag';
import {
    SOCIAL_CREDIT_SETTINGS_DEFAULT, SOCIAL_SERVICE_SETTINGS_CREDITS_TYPE_KEY, SocialCreditSettings,
} from '@libs/types/src/services/types/social-credit-settings.types';
import { FormatNumberPipe } from '../../../../modules/shared/pipes/format-number.pipe';
import { WorkCreditGroupStore } from './work-credit-group.store';
import { creditOffersFrag, workCreditGroupFragment, workCreditPaymentsFragment } from './work-credit-group.query';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import {
    SocialServiceOfferGroupObject,
} from '@libs/types/src/services/object-type/service/social-service/social-service-offer-group.object';
import {
    WorkOfferTableItem,
} from '../social-service-select-offer/social-service-select-offer-table/social-service-select-offer-table.types';
import Decimal from '@libs/shared/utils/decimal';
import {
    SocialCreditPreservedPayment, SocialCreditWorkOffer,
} from '@libs/types/src/services/types/social-credit-work.types';
import {
    getCreditOfferAmount, getPreservedCreditPayments, getReleasedCreditAmount,
} from '@libs/types/src/services/utils/social-credit-work.utils';
import { SocialCreditObject } from '@libs/types/src/services/object-type/folder/social-credit.object';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import { translate } from '../../../../modules/shared/shared.global-service';
import {
    SocialCreditPurchaseDialogComponent,
} from '../../folder/social-credit/social-credit-purchase-dialog/social-credit-purchase-dialog.component';

@Component({
    selector: 'app-work-credit-group',
    templateUrl: './work-credit-group.component.html',
    styleUrl: './work-credit-group.component.scss',
    providers: [WorkCreditGroupStore],
    imports: [TranslatePipe, FormatNumberPipe],
})
export class WorkCreditGroupComponent {
    protected readonly store = inject(WorkCreditGroupStore);
    private readonly dialogs = inject(BaseDialogService);

    public readonly serviceId = input<number | null>(null);
    public readonly folderIds = input<number[]>([]);
    public readonly date = input<Date | null>(null);
    public readonly workId = input<number | null>(null);
    public readonly originalOffers = input<SocialCreditWorkOffer[]>([]);
    public readonly replacedWorkIds = input<number[]>([]);

    public readonly creditOffers = computed(() => {
        const offers: Record<number, number[]> = {};
        for (const context of this.contexts()) {
            offers[context.folderId] = this.pricedOffers()
                .filter(offer => offer.groupId === context.group?.id).map(offer => offer.offerId);
        }
        return offers;
    });
    public readonly insufficientBalance = computed(() =>
        this.groups().some(group => group.requiredCredits > group.availableCredits),
    );
    protected readonly groups = computed(() => this.contexts().map(context => {
        const credits = this.calculateCreditUpdate(context.folderId, context.balance, context.group?.id);
        return {
            folderId: context.folderId,
            group: context.group,
            person: context.person,
            balance: context.balance,
            requiredCredits: credits.requiredCredits,
            releasedCredits: credits.releasedCredits,
            availableCredits: credits.availableCredits,
        };
    }));

    private readonly creditPaymentOffers = signal<WorkOfferTableItem[]>([]);
    private readonly contexts = computed(() => this.creditContext.hasValue() ? this.creditContext.value() : []);
    private readonly pricedOffers = computed(() =>
        this.creditOffersResource.hasValue() ? this.creditOffersResource.value() : [],
    );
    private readonly enabled = computed(() => featureOn.SOCIAL_CREDITS &&
        this.store.globalGlobalStore.organizationStore.getServiceSetting<SocialCreditSettings>(
            this.serviceId() || 0, SOCIAL_SERVICE_SETTINGS_CREDITS_TYPE_KEY, SOCIAL_CREDIT_SETTINGS_DEFAULT,
        ).useCard,
    );
    private readonly workPayments = resource({
        params: () => this.getPaymentWorkIds(),
        loader: ({ params }) => Promise.all(params.map(id =>
            this.store.api.getSocialCreditWorkPayments({ id }, workCreditPaymentsFragment),
        )),
    });
    private readonly creditContext = resource({
        params: () => {
            const folderIds = this.folderIds();
            const date = this.date();
            const hasPayments = this.replacedWorkIds().length
                || (this.workId() && this.originalOffers().some(offer => offer.payByCredits === true));
            return (this.enabled() || hasPayments) && folderIds.length && date ? { folderIds, date } : undefined;
        },
        loader: ({ params }) => Promise.all(params.folderIds.map(id => this.loadContext(id, params.date))),
    });
    private readonly creditOffersResource = resource({
        params: () => {
            const groupIds = [...new Set(this.contexts().flatMap(context => context.group ? [context.group.id] : []))];
            const date = this.date();
            return groupIds.length && date ? { groupIds, date } : undefined;
        },
        loader: async ({ params }) => {
            const offers = await Promise.all(params.groupIds.map(id => this.loadGroupOffers(id, params.date)));
            return offers.flat();
        },
    });

    public refreshCreditOffers(offers: WorkOfferTableItem[] | null): void {
        this.creditPaymentOffers.set((offers || []).map(offer => ({
            offerId: offer.offerId,
            applications: offer.applications,
            payByCredits: offer.payByCredits,
            folder: offer.folder ? { id: offer.folder.id } : undefined,
        })));
    }

    public refreshPayments(): void {
        this.creditContext.reload();
        this.workPayments.reload();
    }

    private getPaymentWorkIds(): number[] | undefined {
        const replaced = this.replacedWorkIds();
        if (replaced.length) return [...new Set(replaced)];
        const id = this.workId();
        return id && (this.enabled() || this.originalOffers().some(offer => offer.payByCredits === true))
            ? [id] : undefined;
    }

    private async loadContext(folderId: number, date: Date) {
        const [context, balance] = await Promise.all([
            this.store.api.getSocialCreditContext({ folderId, date }, workCreditGroupFragment),
            this.store.api.getSocialCreditBalance({ id: folderId }),
        ]);
        return {
            folderId,
            group: context.folderGroup?.group ?? null,
            person: context.folderGroup?.folder.person,
            balance,
        };
    }

    private async loadGroupOffers(groupId: number, date: Date): Promise<SocialServiceOfferGroupObject[]> {
        const filter = new FilterBuilder({ groupId }).validAt(date).singlePage();
        const offers = await this.store.api.getSocialServiceOfferGroups(filter, creditOffersFrag);
        const items: SocialServiceOfferGroupObject[] = offers.items || [];
        return items.filter(offer => offer.creditPrice != null);
    }

    private calculateCreditUpdate(folderId: number, balance: number, groupId?: number) {
        const prices = this.pricedOffers().filter(offer => offer.groupId === groupId);
        const defaultFolderId = this.folderIds().length === 1 ? this.folderIds()[0] : null;
        const offers = this.creditPaymentOffers().filter(offer => (offer.folder?.id ?? defaultFolderId) === folderId);
        const context = this.workId() && !this.replacedWorkIds().length && this.workPayments.hasValue()
            ? this.workPayments.value()[0] : null;
        const preserved = context && this.date()
            ? getPreservedCreditPayments(context, offers, folderId, this.date()!, groupId ?? null) : [];
        const preservedIndexes = new Set(preserved.map(item => item.offerIndex));
        const releasedCredits = this.getReleasedCredits(folderId, preserved);
        let total = new Decimal(0);
        for (const [index, offer] of offers.entries()) {
            if (preservedIndexes.has(index)) continue;
            // Eligible activities have credit payment checked by default.
            if (offer.payByCredits === false) continue;
            const price = prices.find(item => item.offerId === offer.offerId)?.creditPrice;
            if (price == null) continue;
            total = total.add(new Decimal(getCreditOfferAmount(offer.applications || 0, price)));
        }
        return {
            requiredCredits: Number(total.toString()),
            releasedCredits,
            availableCredits: Number(new Decimal(balance).add(new Decimal(releasedCredits)).toString()),
        };
    }

    private getReleasedCredits(folderId: number, preserved: SocialCreditPreservedPayment[]): number {
        const contexts = this.workPayments.hasValue() ? this.workPayments.value() : [];
        const released = contexts.reduce((sum, context) =>
            sum.add(new Decimal(getReleasedCreditAmount(context, preserved, folderId))), new Decimal(0),
        );
        return Number(released.toString());
    }

    protected purchaseCredits(folderId: number, requiredCredits: number, availableCredits: number): void {
        const amount = Number(new Decimal(requiredCredits).subtract(new Decimal(availableCredits)).toString());
        this.dialogs.open(SocialCreditPurchaseDialogComponent, {
            header: translate.instant('socialCredits.purchase.title'),
            styleClass: 'p-dialog-width-30',
            data: { folderId, serviceId: this.serviceId(), amount },
        }).onClose.subscribe((result?: SocialCreditObject) => {
            if (result) this.refreshPayments();
        });
    }

    protected readonly canPurchase = this.store.isAllowed('socialCredits.create');
}
